import React from "react";
import { useWindowDimensions } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  useAnimatedReaction,
  withTiming,
  withDelay,
  withRepeat,
  Easing,
  cancelAnimation,
  clamp,
  interpolate,
  Extrapolation,
  type SharedValue,
} from "react-native-reanimated";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import LBox from "@components/LBox";
import { getViewPort } from "@utils/web/config";

const SLIDE_DURATION = 2500;
const TRANSITION_DURATION = 600;
const SWIPE_THRESHOLD = 0.25;
const SWIPE_DEADZONE = 10;
const PHASE = SLIDE_DURATION + TRANSITION_DURATION;
const TOTAL_CYCLE = PHASE * 2;

const slides = [
  {
    key: "1",
    source: require("@assets/onboarding/login1.webp"),
    aspectRatio: 738 / 735,
  },
  {
    key: "2",
    source: require("@assets/onboarding/login2.webp"),
    aspectRatio: 676 / 726,
  },
];

const DOT_SIZE = 6;
const ACTIVE_WIDTH = 18;

function easeOutCubic(t: number): number {
  "worklet";
  const inv = 1 - t;
  return 1 - inv * inv * inv;
}

function makeClockAnimation() {
  "worklet";
  return withRepeat(
    withTiming(TOTAL_CYCLE, {
      duration: TOTAL_CYCLE,
      easing: Easing.linear,
    }),
    -1,
    false,
  );
}

const Indicator = ({
  index,
  activeIndex,
  progress,
}: {
  index: number;
  activeIndex: SharedValue<number>;
  progress: SharedValue<number>;
}) => {
  const containerStyle = useAnimatedStyle(() => ({
    width: withTiming(
      activeIndex.value === index ? ACTIVE_WIDTH : DOT_SIZE,
      { duration: 200 },
    ),
    height: DOT_SIZE,
    borderRadius: DOT_SIZE / 2,
    backgroundColor: "rgba(255,255,255,0.3)",
    overflow: "hidden" as const,
  }));

  const fillStyle = useAnimatedStyle(() => {
    const isActive = activeIndex.value === index;
    const isPast = index < activeIndex.value;
    const fillFraction = isActive ? progress.value : isPast ? 1 : 0;
    const maxWidth = isActive ? ACTIVE_WIDTH : DOT_SIZE;
    return {
      position: "absolute" as const,
      left: 0,
      top: 0,
      bottom: 0,
      width: fillFraction * maxWidth,
      backgroundColor: "white",
      borderRadius: DOT_SIZE / 2,
    };
  });

  return (
    <Animated.View style={containerStyle}>
      <Animated.View style={fillStyle} />
    </Animated.View>
  );
};

const Slide = ({
  slide,
  index,
  translateX,
  screenWidth,
}: {
  slide: (typeof slides)[number];
  index: number;
  translateX: SharedValue<number>;
  screenWidth: number;
}) => {
  const animStyle = useAnimatedStyle(() => {
    const inputRange = [
      -(index + 1) * screenWidth,
      -index * screenWidth,
      -(index - 1) * screenWidth,
    ];
    const scale = interpolate(
      translateX.value,
      inputRange,
      [0.85, 1, 0.85],
      Extrapolation.CLAMP,
    );
    const opacity = interpolate(
      translateX.value,
      inputRange,
      [0.5, 1, 0.5],
      Extrapolation.CLAMP,
    );
    return { transform: [{ scale }], opacity };
  });

  return (
    <Animated.View style={[{ width: screenWidth, height: "100%" }, animStyle]}>
      <Animated.Image
        source={slide.source}
        style={{ width: "100%", height: "100%" }}
        resizeMode="contain"
      />
    </Animated.View>
  );
};

const LoginCarousel = ({ startDelay = 0 }: { startDelay?: number }) => {
  const window = useWindowDimensions();
  const { width: screenWidth } = getViewPort(window);
  const translateX = useSharedValue(0);
  const activeIndex = useSharedValue(0);
  const progress = useSharedValue(0);
  const gestureStartX = useSharedValue(0);

  const clock = useSharedValue(0);
  const clockStartIndex = useSharedValue(0);
  const clockRunning = useSharedValue(false);
  const swipeHandled = useSharedValue(false);

  // Derive translateX, activeIndex, progress from the single clock
  useAnimatedReaction(
    () => (clockRunning.value ? clock.value : -1),
    (t) => {
      if (t < 0) return;
      const phase = t % TOTAL_CYCLE;
      const phaseIndex = Math.floor(phase / PHASE);
      const phaseTime = phase % PHASE;
      const currentSlide =
        (clockStartIndex.value + phaseIndex) % slides.length;
      const nextSlide = (currentSlide + 1) % slides.length;

      if (phaseTime < SLIDE_DURATION) {
        const targetX = -currentSlide * screenWidth;
        if (translateX.value !== targetX) translateX.value = targetX;
        if (activeIndex.value !== currentSlide)
          activeIndex.value = currentSlide;
        progress.value = phaseTime / SLIDE_DURATION;
      } else {
        if (activeIndex.value !== nextSlide) activeIndex.value = nextSlide;
        if (progress.value !== 0) progress.value = 0;
        const transT = (phaseTime - SLIDE_DURATION) / TRANSITION_DURATION;
        const eased = easeOutCubic(transT);
        const from = -currentSlide * screenWidth;
        const to = -nextSlide * screenWidth;
        translateX.value = from + (to - from) * eased;
      }
    },
  );

  // One-time initial start
  React.useEffect(() => {
    clockRunning.set(true);
    clock.set(withDelay(startDelay, makeClockAnimation()));
    return () => cancelAnimation(clock);
  }, []);

  const panGesture = Gesture.Pan()
    .activeOffsetX([-10, 10])
    .onTouchesDown(() => {
      "worklet";
      clockRunning.set(false);
      swipeHandled.value = false;
      cancelAnimation(clock);
      cancelAnimation(translateX);
      gestureStartX.value = translateX.value;
      progress.set(0);
    })
    .onStart(() => {
      "worklet";
      swipeHandled.value = true;
    })
    .onTouchesUp(() => {
      "worklet";
      // Only restart if gesture never activated (tap/hold without swipe)
      if (!swipeHandled.value && !clockRunning.value) {
        const currentSlide = Math.round(-translateX.value / screenWidth);
        clockStartIndex.set(currentSlide);
        clock.set(0);
        clockRunning.set(true);
        clock.set(makeClockAnimation());
      }
    })
    .onUpdate((e) => {
      "worklet";
      // Apply deadzone so small movements don't shift slides
      const raw = e.translationX;
      const tx =
        Math.abs(raw) > SWIPE_DEADZONE
          ? raw - Math.sign(raw) * SWIPE_DEADZONE
          : 0;
      const maxTranslate = -(slides.length - 1) * screenWidth;
      translateX.set(clamp(
        gestureStartX.value + tx,
        maxTranslate,
        0,
      ));
      activeIndex.set(Math.round(-translateX.value / screenWidth));
    })
    .onEnd((e) => {
      "worklet";
      const didSwipe = Math.abs(e.translationX) > SWIPE_DEADZONE;

      if (didSwipe) {
        // Determine target slide
        const currentPos = -translateX.value / screenWidth;
        let newIndex = Math.round(currentPos);

        if (
          e.translationX < -screenWidth * SWIPE_THRESHOLD ||
          e.velocityX < -500
        ) {
          newIndex = Math.min(Math.ceil(currentPos), slides.length - 1);
        } else if (
          e.translationX > screenWidth * SWIPE_THRESHOLD ||
          e.velocityX > 500
        ) {
          newIndex = Math.max(Math.floor(currentPos), 0);
        }

        newIndex = Math.max(0, Math.min(newIndex, slides.length - 1));
        activeIndex.set(newIndex);

        // Snap, then restart clock in completion callback
        translateX.set(withTiming(
          -newIndex * screenWidth,
          {
            duration: TRANSITION_DURATION,
            easing: Easing.out(Easing.cubic),
          },
          (finished) => {
            "worklet";
            if (finished) {
              clockStartIndex.set(newIndex);
              clock.set(0);
              clockRunning.set(true);
              clock.set(makeClockAnimation());
            }
          },
        ));
      } else {
        // Tap / hold-and-release — restart immediately
        const currentSlide = Math.round(-translateX.value / screenWidth);
        clockStartIndex.set(currentSlide);
        clock.set(0);
        clockRunning.set(true);
        clock.set(makeClockAnimation());
      }
    });

  const rowStyle = useAnimatedStyle(() => ({
    flexDirection: "row" as const,
    height: "100%",
    transform: [{ translateX: translateX.value }],
  }));

  return (
    <LBox
      style={{
        flex: 1,
        overflow: "hidden",
        width: screenWidth,
        marginHorizontal: -16,
      }}
    >
      <GestureDetector gesture={panGesture}>
        <Animated.View style={rowStyle}>
          {slides.map((slide, i) => (
            <Slide
              key={slide.key}
              slide={slide}
              index={i}
              translateX={translateX}
              screenWidth={screenWidth}
            />
          ))}
        </Animated.View>
      </GestureDetector>
      <LBox
        position="absolute"
        left={0}
        right={0}
        style={{ bottom: "3%" }}
        flexDirection="row"
        justifyContent="center"
        alignItems="center"
        gap="s1"
      >
        {slides.map((slide, i) => (
          <Indicator
            key={slide.key}
            index={i}
            activeIndex={activeIndex}
            progress={progress}
          />
        ))}
      </LBox>
    </LBox>
  );
};

export default LoginCarousel;
